import { useState } from "react";
import { useBlocker, useNavigate } from "react-router-dom";
import { UnsavedChangesModal } from "../elements/UnsavedChangesModal";
import { useWarnBeforeUnload } from "../hooks/useWarnBeforeUnload";
import { EntityPageUnsavedChangesProps } from "./EntityPageUnsavedChanges.types";

const LeaveMessage =
  "You have unsaved changes to this record. Do you want to save them before " +
  "leaving this page?";

const SwitchMessage =
  "You have unsaved changes to this record. Do you want to save them before " +
  "switching view?";

interface Location {
  pathname: string;
  search: string;
}

/** Another page, or this page for another record. Changing only the view is neither. */
function leavesRecord(current: Location, next: Location) {
  return (
    current.pathname !== next.pathname ||
    new URLSearchParams(current.search).get("id") !==
      new URLSearchParams(next.search).get("id")
  );
}

/**
 * Asks before unsaved edits to a record are lost: by leaving it, by switching view
 * (which re-reads the record), or by closing or refreshing the tab.
 */
export function EntityPageUnsavedChanges({
  isDirty,
  save,
  allowNavigationRef,
  pendingFormId,
  onSwitch,
  onCancelSwitch,
}: EntityPageUnsavedChangesProps) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  useWarnBeforeUnload(isDirty);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !allowNavigationRef.current &&
      isDirty &&
      leavesRecord(currentLocation, nextLocation)
  );

  if (blocker.state === "blocked") {
    const target = blocker.location;

    // Saving a new record puts its id in the url, which clears the blocker, so the way
    // out is navigated to rather than proceeded with.
    const saveAndLeave = async () => {
      setSaving(true);

      try {
        if (await save()) {
          allowNavigationRef.current = true;
          navigate(
            { pathname: target.pathname, search: target.search, hash: target.hash },
            { state: target.state }
          );
        } else {
          // Refused, so the problems it found are left on the page to be fixed.
          blocker.reset();
        }
      } finally {
        setSaving(false);
      }
    };

    return (
      <UnsavedChangesModal
        isSubmitting={saving}
        message={LeaveMessage}
        saveDescription="Save this record, then leave the page"
        onCancel={() => blocker.reset()}
        onContinueWithoutSaving={() => blocker.proceed()}
        onSaveAndContinue={saveAndLeave}
      />
    );
  }

  if (pendingFormId !== null) {
    const saveAndSwitch = async () => {
      setSaving(true);

      try {
        if (await save()) {
          onSwitch(pendingFormId);
        } else {
          onCancelSwitch();
        }
      } finally {
        setSaving(false);
      }
    };

    return (
      <UnsavedChangesModal
        isSubmitting={saving}
        message={SwitchMessage}
        saveDescription="Save this record, then switch view"
        discardDescription="Discard your changes and switch view"
        onCancel={onCancelSwitch}
        onContinueWithoutSaving={() => onSwitch(pendingFormId)}
        onSaveAndContinue={saveAndSwitch}
      />
    );
  }

  return null;
}

export default EntityPageUnsavedChanges;
